<template>
  <div class="wrapper">

    <div class="header-wrapper">
      <v-toolbar color="primary" dark max-height="56">
        <v-btn @click="routerBack" icon>
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>

        <v-toolbar-title
          style="
            position: absolute;
            left: 50%;
            transform: translateX(-50%);
            margin: 0;
            padding: 0;
            max-width: 70%;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          "
          class="text-center"
        >
          {{ childName }}
        </v-toolbar-title>

        <div style="width: 60px; margin-left: auto;">
          <v-btn
            icon
            @click="showDatePicker"
          >
            <v-icon large>mdi-calendar-filter</v-icon>
          </v-btn>
        </div>
      </v-toolbar>
    </div>

    <div v-if="fetchLoading" class="d-flex justify-center align-center fill-height pa-10">
      <vue-loading
        :active="fetchLoading"
        loader="bars"
        color="#EE6090"
        :size="{ width: '30px', height: '30px' }"
      ></vue-loading>
    </div>

    <div class="content-wrapper no-scroll-bar" v-else ref="scrollTarget">
      <v-list lines="two" density="compact">
        <v-list-item
          v-for="g in groupChats"
          :key="g.chatId"
          :value="g.chatId"
          link
          @click="toDetail(g.chatId)"
        >
          <v-list-item-title class="font-weight-bold subtitle-1">
            {{ displayTitle(g) }}
          </v-list-item-title>

          <v-list-item-subtitle class="subtitle-2 text-grey-darken-5">
            {{ dayFormat(g.createdAt) }}
          </v-list-item-subtitle>

          <template v-slot:append>
            <div
              v-if="g.hasAttachment"
              class="badge d-flex justify-center align-center mr-2"
            >
              <v-icon>mdi-file-image</v-icon>
            </div>
          </template>

        </v-list-item>

        <v-divider />
      </v-list>

      <div
        class="d-flex justify-center pa-5"
        v-if="isFetchMoreAttachment"
      >
        <v-btn
          @click="fetchMoreAttachment"
          color="primary"
          text
          :loading="moreFetchLoading"
        >
          もっと見る
        </v-btn>
      </div>
    </div>

    <div class="date-picker">
      <v-dialog v-model="showPicker" max-width="400" persistent>
        <v-card>
          <v-card-title class="text-center pt-4">年月を選択</v-card-title>
    
          <v-card-text>
            <v-row>
              <v-col cols="6">
                <div class="text-caption text-center mb-2">年</div>
                <v-list scrollable-y style="height: 250px; scroll-behavior: smooth;">
                  <v-list-item
                    v-for="year in yearOptions"
                    :key="year"
                    :ref="el => { if (tempYear === year) setYearRef(el) }"
                    :active="tempYear === year"
                    color="primary"
                    @click="selectYear(year)"
                    class="text-center"
                  >
                    <v-list-item-title>{{ year }}年</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-col>

              <v-col cols="6">
                <div class="text-caption text-center mb-2">月</div>
                <v-list scrollable-y style="height: 250px; scroll-behavior: smooth;">
                  <v-list-item
                    v-for="month in 12"
                    :key="month"
                    :ref="el => { if (tempMonth === month) setMonthRef(el) }"
                    :active="tempMonth === month"
                    color="primary"
                    @click="selectMonth(month)"
                    class="text-center"
                  >
                    <v-list-item-title>{{ month }}月</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-col>
            </v-row>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions>
            <v-btn variant="text" @click="showPicker = false">キャンセル</v-btn>
            <v-btn variant="text" color="blue" @click="dateReset">クリア</v-btn>
            <v-btn variant="text" color="primary" @click="dateSelected">決定</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>

  </div>
</template>
<script lang="ts">
import { defineComponent, nextTick } from 'vue';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
dayjs.locale('ja');

import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/css/index.css';

import { attachmentModule } from '@/store/entityModules/attachment';
import { resourceModule } from '@/store/entityModules/resource';
import { chatModule } from '@/store/entityModules/chat';

import {
  HomeAttachmentResponse,
} from '@API/src/component/home/attachment/types';

interface DataType {
  moreCnt: number;
  tempYear: number;
  tempMonth: number;
  yearOptions: number[];
  childId: number;
  childName: string;
  fetchLoading: boolean;
  moreFetchLoading: boolean;
  isFetchMoreAttachment: boolean;
  isAttachmentUrls: boolean;
  imgLoading: boolean;
  showingUrls: string[];
  showPicker: boolean;
  selectedDate: string;
  activeYearEl: any;
  activeMonthEl: any;
}

export default defineComponent({
  components: {
    'vue-loading': Loading,
  },

  data: (): DataType =>({
    moreCnt: 0,
    tempYear: new Date().getFullYear(),
    tempMonth: new Date().getMonth() + 1,
    yearOptions: Array.from({ length: 11 }, (_, i) => new Date().getFullYear() - 5 + i),
    childId: NaN,
    childName: '',
    fetchLoading: true,
    moreFetchLoading: false,
    isFetchMoreAttachment: true,
    imgLoading: false,
    isAttachmentUrls: false,
    showingUrls: [],
    showPicker: false,
    selectedDate: localStorage.selectedDate,
    activeYearEl: null,
    activeMonthEl: null,
  }),

  computed: {
    groupChats() {
      return chatModule.groupChats;
    },
    groupChatsTotal() {
      return chatModule.groupChatsCount;
    },
    // 詳細画面で翻訳した内容（localStorage）を読み込む
    translatedChats(): Record<number, string> {
      if (!this.childId || Number.isNaN(this.childId)) return {};
      try {
        const raw = localStorage.getItem(this.getTranslatedChatsKey());
        return raw ? JSON.parse(raw) : {};
      } catch {
        return {};
      }
    },
  },

  methods: {
    getTranslatedChatsKey(): string {
      return `translatedChats_${this.childId}`;
    },
    // 翻訳済みデータがある場合は翻訳後のタイトル（1行目）を表示する
    displayTitle(chat: { chatId: number; title: string | null }): string {
      const translated = this.translatedChats[chat.chatId];
      if (!translated) return chat.title ?? '';
      const idx = translated.indexOf('\n');
      return idx === -1 ? translated : translated.slice(0, idx);
    },

    // 動的refの紐付け用関数
    setYearRef(el: any) {
      this.activeYearEl = el;
    },
    setMonthRef(el: any) {
      this.activeMonthEl = el;
    },

    // 中央にスクロールさせる共通処理
    async scrollToActiveItems(smooth = false) {
      await nextTick();
      const behavior = smooth ? 'smooth' : 'auto';
      
      // 年リストのスクロール
      if (this.activeYearEl) {
        const el = this.activeYearEl.$el || this.activeYearEl;
        el.scrollIntoView({ block: 'center', behavior: behavior });
      }
      
      // 月リストのスクロール
      if (this.activeMonthEl) {
        const el = this.activeMonthEl.$el || this.activeMonthEl;
        el.scrollIntoView({ block: 'center', behavior: behavior });
      }
    },

    // ダイアログを開くときに今の値をセットしてスクロール
    async showDatePicker() {
      if (this.selectedDate) {
        const d = new Date(this.selectedDate);
        this.tempYear = d.getFullYear();
        this.tempMonth = d.getMonth() + 1;
      } else {
        this.tempYear = new Date().getFullYear();
        this.tempMonth = new Date().getMonth() + 1;
      }
      
      // 1. まずダイアログを表示させてDOMを作らせる
      this.showPicker = true;
      
      // 2. ダイアログの表示アニメーションが始まる瞬間に、一瞬（behavior: 'auto'）でスクロール
      // わずかなラグを考慮して、nextTickの後に即座に実行
      await nextTick();
      this.scrollToActiveItems(false);
    },

    // 年を手動で選択した時（アニメーションありで中央へ）
    async selectYear(year: number) {
      this.tempYear = year;
      await nextTick();
      if (this.activeYearEl) {
        const el = this.activeYearEl.$el || this.activeYearEl;
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    },

    // 月を手動で選択した時（アニメーションありで中央へ）
    async selectMonth(month: number) {
      this.tempMonth = month;
      await nextTick();
      if (this.activeMonthEl) {
        const el = this.activeMonthEl.$el || this.activeMonthEl;
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    },

    routerBack() {
      this.$router.replace('/record');
    },

    toDetail(chatId: number) {
      const el = this.$refs.scrollTarget as HTMLElement;
      const scrollY = el?.scrollTop ?? 0;
      const moreCnt = this.moreCnt;

      this.$router.push({
        path: "/references/detail",
        query: {
          chatId: String(chatId),
          childId: String(this.childId),
          childName: this.childName,
        },
        state: { scrollY, moreCnt }
      })
    },

    dayFormat(date: string) {
      return dayjs(date).format('YYYY/MM/DD');
    },

    async fetchGroupChats() {
      this.fetchLoading = true;
      chatModule.removeGroupChats();
      await chatModule.fetchGroupChats({
        childId: this.childId,
        limit: 10,
        skip: 0,
        date: this.selectedDate,
      });
      if (this.groupChats.length >= this.groupChatsTotal) {
        this.isFetchMoreAttachment = false;
      }
      this.fetchLoading = false;
    },

    async fetchMoreAttachment() {
      this.moreCnt++;
      await this.fetchMoreAttachmentMain();
    },

    async fetchMoreAttachmentMain() {
      this.moreFetchLoading = true;
      await chatModule.fetchGroupChats({
        childId: this.childId,
        limit: 10,
        skip: this.groupChats.length,
        date: this.selectedDate,
      });
      if (this.groupChats.length >= this.groupChatsTotal) {
        this.isFetchMoreAttachment = false;
      }
      this.moreFetchLoading = false;
    },

    insertStr(str, index, insert) {
      return str.slice(0, index) + insert + str.slice(index, str.length);
    },
    formatAmzIso(dateStr: string): string {
      let formatted = dateStr;
      formatted = this.insertStr(formatted, 4, '-');
      formatted = this.insertStr(formatted, 7, '-');
      formatted = this.insertStr(formatted, 13, ':');
      formatted = this.insertStr(formatted, 16, ':');
      return formatted;
    },
    isPresignedUrlExpired(url: string): boolean {
      try {
        const arr = url.split(/[=&]/);
        if (arr.length >= 12) {
          const XAmzDate = dayjs(this.formatAmzIso(arr[5])).locale('ja');
          const XAmzExpires = Number(arr[7]);
          const ExpirationTime = dayjs(XAmzDate).add(XAmzExpires, 'seconds');
          const Now = dayjs();
          return Now.isAfter(ExpirationTime);
        } else {
          return true;
        }
      } catch (error) {
        return true;
      }
    },
    async openImg(attachment: HomeAttachmentResponse) {
      let img = attachment.attachmentUrl;
      const isImgExpired = this.isPresignedUrlExpired(img);
      if (isImgExpired) {
        this.imgLoading = true;
        const objectKey = attachment.objectKey;
        const imgContent = await resourceModule.postResourceDownload({
          objectKey,
        });
        img = imgContent.preSignedUrl;
        this.imgLoading = false;
      }
      const ua = navigator.userAgent;
      const isIOS = ua.indexOf('iPhone') >= 0
        || ua.indexOf('iPad') >= 0
        || Number(navigator.userAgent.indexOf('iPod')) >= 0;
      if (isIOS) {
        if ((window as any).webkit.messageHandlers) {
          (window as any).webkit.messageHandlers.pdf.postMessage(`${[img]}`);
        }
      } else {
        this.isAttachmentUrls = true;
        this.showingUrls = [img];
      }
    },
    closeChatImg() {
      this.isAttachmentUrls = false;
      this.showingUrls = [];
    },
    dateSelected() {
      this.moreCnt = 0;
      this.isFetchMoreAttachment = true;

      const date = new Date(this.tempYear, this.tempMonth - 1, 1);
      this.showPicker = false;
      localStorage.selectedDate = dayjs(date).format('YYYY-MM');
      sessionStorage.setItem('referenceDate', dayjs(date).format('YYYY-MM'));
      this.selectedDate = localStorage.selectedDate;

      if (typeof this.$route.query.childId === 'string') {
        this.childId = Number(this.$route.query.childId);
        this.fetchGroupChats();
      } else {
        this.routerBack();
      }
    },
    dateReset() {
      this.moreCnt = 0;
      this.isFetchMoreAttachment = true;

      this.showPicker = false;
      localStorage.selectedDate = '';
      sessionStorage.setItem('referenceDate', '');
      this.selectedDate = localStorage.selectedDate;

      if (typeof this.$route.query.childId === 'string') {
        this.childId = Number(this.$route.query.childId);
        this.fetchGroupChats();
      } else {
        this.routerBack();
      }
    },
  },

  async mounted() {
    if (typeof this.$route.query.childName === 'string') {
      this.childName = this.$route.query.childName;
    }
    const referenceDate = sessionStorage.getItem('referenceDate');
    if (referenceDate) {
      localStorage.selectedDate = referenceDate;
      this.selectedDate = localStorage.selectedDate;
    }
    if (typeof this.$route.query.childId === 'string') {
      this.childId = Number(this.$route.query.childId);
      await this.fetchGroupChats();
    } else {
      this.routerBack();
    }

    if (localStorage.selectedDate) {
      this.selectedDate = localStorage.selectedDate;
    }

    this.moreCnt = window.history.state?.moreCnt ? Number(window.history.state?.moreCnt) : 0;
    for (let i = 0; i < this.moreCnt; i++) {
      await this.fetchMoreAttachmentMain();
    }

    const savedScrollY = window.history.state?.scrollY;
    if (savedScrollY) {
      await nextTick();
      const el = this.$refs.scrollTarget as HTMLElement | undefined;
      if (el) {
        el.scrollTop = savedScrollY;
      }
    }
  },
});
</script>

<style lang="scss" scoped>
.wrapper {
  height: 100%;
  overflow-y: scroll;
  scroll-behavior: smooth;
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
    -webkit-appearance: none;
  }
  @supports (bottom: env(safe-area-inset-bottom)) {
    padding-top: env(safe-area-inset-top);
  }

  .header-wrapper {
    height: 56px;
    width: 100%;
    position: fixed;
    z-index: 3;

    .date_card {
      background-color: #ffffff;
    }
    .outline {
      border: 1px solid rgb(var(--v-theme-primary));
    }
  }
  .grey-border {
    height: 2px;
    width: 100%;
    background-color: #F5F6F6;
  }

  .content-wrapper {
    height: calc(calc(var(--vh, 1vh) * 100) - 56px - 56px);
    margin: 64px 0 56px;
    overflow-y: scroll;
  }

  .date-picker {
    position: absolute;
    top: 57px;
    right: 0;
    z-index: 99;
    border-radius: 4px;
    box-shadow: 0 5px 5px -3px rgba(0, 0, 0, .2), 0 8px 10px 1px rgba(0, 0, 0, .14), 0 3px 14px 2px rgba(0, 0, 0, .12);
  }
}
.centered-title {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: auto;
}
</style>